import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  FinanceJournalDraftEntryFilter,
  ProjectionKind,
  useJournalDraftEntryDatable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { Filter } from 'modules/core/components/DataTable/types'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  getJournalEntryDraftStatusOptions,
  getJournalEntryTypeOptions,
  mapTypeText,
} from 'modules/finance/lib/journalEntries'

import { Amount } from '../../JournalsEntry/Datatable/_Amount'
import { Insights } from '../Insights'
import Actions from './_Actions'
import { Status } from './_Status'

interface Props {
  legalEntityId?: number
  companyId: number
  defaultLegalEntityId?: number
  legalEntitiesOptions: { label: string; value: number }[]
  onFilterChange: (data: Record<string, unknown>) => void
  onClickDetail: (journalDraftEntryId: number) => void
}

const JournalDraftEntryDatatable = ({
  legalEntityId,
  companyId,
  legalEntitiesOptions,
  onFilterChange,
  defaultLegalEntityId,
  onClickDetail,
}: Props) => {
  const onUpdateLegalEntityFilter = (filter?: Filter<number>) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const filterValue = filter?.value as number | undefined
    // Clear filter
    if (filter === undefined || filterValue === undefined) {
      onFilterChange?.({ legalEntityId: undefined })
      return
    }
    onFilterChange?.({ legalEntityId: filterValue })
  }
  const { referenceDateFrom, referenceDateTo } = useSearchParams<{
    referenceDateFrom?: string
    referenceDateTo?: string
  }>()

  const source = useJournalDraftEntryDatable(
    [
      {
        identifier: 'referenceDate',
        title: i18n.t('finance.journal_entry.datatable.fields.reference_date'),
        sortBy: 'referenceDate',
        kind: ProjectionKind.Custom,
        render: ({ referenceDate }) => (
          <Text.Normal size="200">
            {referenceDate ? dateBackendDateTimeToUI(String(referenceDate)) : '-'}
          </Text.Normal>
        ),
        template: '{{ referenceDate }}',
      },
      {
        identifier: 'type',
        title: i18n.t('finance.journal_entry.datatable.fields.type'),
        sortBy: 'type',
        kind: ProjectionKind.Custom,
        render: ({ type }) => <Text.Normal size="200">{mapTypeText(type)}</Text.Normal>,
        template: '{{ type }}',
      },
      {
        identifier: 'description',
        title: i18n.t('finance.journal_entry.datatable.fields.description'),
        kind: ProjectionKind.Custom,
        colSpan: 2,
        render: ({ description, financialDocument }) => (
          <Text.Normal size="200" ellipsis overflowWrap lineClamp={4}>
            {description ? description : financialDocument ? financialDocument.documentNumber : '-'}
          </Text.Normal>
        ),
        template: '{{ description }}',
      },
      {
        identifier: 'status',
        title: i18n.t('finance.journal_entry.datatable.fields.status'),
        kind: ProjectionKind.Custom,
        sortBy: 'status',
        render: ({ status }) => <Status status={status} />,
        template: '{{ status }}',
      },

      {
        identifier: 'document',
        title: i18n.t('finance.journal_entry.datatable.fields.document'),
        kind: ProjectionKind.Custom,
        hidden: true,
        render: ({ financialDocument }) => (
          <Text.Normal size="200" ellipsis overflowWrap lineClamp={4}>
            {financialDocument ? financialDocument.documentNumber : '-'}
          </Text.Normal>
        ),
        template: '{{ financialDocument }}',
      },
      {
        width: 140,
        identifier: 'lines',
        title: i18n.t('finance.journal_entry.datatable.fields.lines_v2'),
        kind: ProjectionKind.Custom,
        render: ({ journalDraftLines }) => (
          <Tag type="grey" inline>
            {journalDraftLines.length}
          </Tag>
        ),
        template: '{{ journalDraftLines.size }}',
        tooltip: i18n.t('finance.journal_entry.datatable.fields.lines_tooltip'),
      },
      {
        identifier: 'debit',
        title: i18n.t('finance.journal_entry.datatable.fields.debit'),
        kind: ProjectionKind.Custom,
        render: ({ journalDraftLines, legalEntity_currency }) => {
          const totalDebit = journalDraftLines.reduce(
            (sum: number, line: { debitAmountCents?: number | null }) => {
              const credit = line.debitAmountCents || 0
              return sum + credit
            },
            0
          )
          return <Amount amountCents={totalDebit} currency={legalEntity_currency} />
        },
        template: '{{ - }}',
      },
      {
        identifier: 'credit',
        title: i18n.t('finance.journal_entry.datatable.fields.credit'),
        kind: ProjectionKind.Custom,
        render: ({ journalDraftLines, legalEntity_currency }) => {
          const totalCredit = journalDraftLines.reduce(
            (sum: number, line: { creditAmountCents?: number | null }) => {
              const credit = line.creditAmountCents || 0
              return sum + credit
            },
            0
          )

          return <Amount amountCents={totalCredit} currency={legalEntity_currency} />
        },
        template: '{{ - }}',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 50,
        render: ({ id }: { id: number }) => {
          return <Actions journalDraftEntryId={id} onClickDetail={onClickDetail} />
        },
      },
    ],
    {
      variables: { legalEntityIds: legalEntityId ? [legalEntityId] : [], companyId: companyId },
    }
  )

  const filters: DataTableFilter<FinanceJournalDraftEntryFilter>[] = [
    {
      identifier: 'legal_entity_id',
      title: i18n.t('finance.workspace.legal_entity_scope.selector_label'),
      path: 'legalEntityId',
      default: !!defaultLegalEntityId,
      type: FilterType.Integer,
      onUpdateValue: onUpdateLegalEntityFilter,
      categories: [
        {
          kind: 'SingleChoice',
          operation: 'in',
          value: defaultLegalEntityId,
          options: legalEntitiesOptions,
        },
      ],
    },
    {
      identifier: 'type',
      title: i18n.t('finance.journal_entry.datatable.fields.type'),
      path: 'type',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: getJournalEntryTypeOptions(),
        },
      ],
    },
    {
      identifier: 'status',
      title: i18n.t('finance.journal_entry.datatable.fields.status'),
      path: 'status',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: getJournalEntryDraftStatusOptions(),
        },
      ],
    },
    {
      identifier: 'reference_daate',
      title: i18n.t('finance.journal_entry.datatable.fields.reference_date'),
      path: 'referenceDate',
      default: !!referenceDateFrom && !!referenceDateTo,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
          value:
            !!referenceDateFrom && !!referenceDateTo
              ? [referenceDateFrom, referenceDateTo]
              : undefined,
        },
      ],
    },
  ]

  return (
    <DataTable.Provider
      source={source}
      persistFilters={false}
      pagination={{ kind: 'offset', itemsPerPage: 25 }}
      sort={{ path: 'referenceDate', direction: 'asc' }}
      filters={filters}
      enableClearFilters
    >
      <Box gap="s16">
        <DataTable.HeadBar disableExport />
        <Insights companyId={companyId}></Insights>
        <DataTable.Table />
      </Box>
    </DataTable.Provider>
  )
}

export default JournalDraftEntryDatatable
